import { Link } from 'react-router-dom';
import { Sparkles, ArrowRight } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useSimilarCareers } from '@/hooks/useSimilarCareers';
import { generateSalaryPageUrl } from '@/lib/occupationUtils';
import { toTitleCase } from '@/lib/utils';

interface SimilarCareersProps {
  soc?: string;
  location?: string;
  className?: string;
}

/**
 * "Similar careers" powered by the optional pgvector AI layer. Renders nothing
 * until embeddings exist (api-similar-careers returns available: false), so the
 * occupation page stays clean before the AI layer is provisioned.
 */
const SimilarCareers = ({ soc, location = 'united-states', className = '' }: SimilarCareersProps) => {
  const { data } = useSimilarCareers(soc);

  if (!data?.available || !data.similar || data.similar.length === 0) {
    return null;
  }

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-wage-primary" />
          Similar careers
        </CardTitle>
        <CardDescription>Roles with comparable skills and day-to-day work.</CardDescription>
      </CardHeader>
      <CardContent>
        <ul className="divide-y">
          {data.similar.map((career) => (
            <li key={career.soc_code}>
              <Link
                to={generateSalaryPageUrl({ title: career.occ_title, soc: career.soc_code }, location)}
                className="flex items-center justify-between py-2.5 group"
              >
                <span className="text-sm font-medium group-hover:text-wage-primary transition-colors">
                  {toTitleCase(career.occ_title)}
                </span>
                <ArrowRight className="h-4 w-4 text-muted-foreground group-hover:text-wage-primary transition-colors" />
              </Link>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
};

export default SimilarCareers;
